import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import ProgramTaskModal from "./ProgramTaskModal";
import "./ProgramDetailsPage.css";
import { Text, Group, Box, Progress, Paper } from "@mantine/core";
import {
  IconUserCheck,
  IconClock,
  IconCalendar,
  IconTrendingUp,
  IconPlayerPlay,
  IconFileText,
  IconStar,
  IconUpload,
  IconQuestionMark,
  IconListCheck,
  IconShieldCheck,
} from "@tabler/icons-react";
import LinkifiedText from "../shared/LinkifiedText/LinkifiedText";

function ProgramDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT } = useAuth();
  const [program, setProgram] = useState(location.state?.program || null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasksError, setTasksError] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [submittingTasks, setSubmittingTasks] = useState(new Set());
  const [activeTab, setActiveTab] = useState("todo");
  const fromTraining = Boolean(location.state?.fromTraining);
  const trainingTab = location.state?.trainingTab || "All Programs";
  const [filePreviewTask, setFilePreviewTask] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);

  useEffect(() => {
    fetchProgramData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProgramData = async () => {
    try {
      setError(null);
      setTasksError("");
      setLoading(true);
      setTasksLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/programs/details/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch program details");
      }

      const result = await response.json();
      setProgram(prev => ({ ...(prev || {}), ...(result.data || {}) }));
      setTasks(result.data.tasks || []);
    } catch (err) {
      setError(err.message);
      setProgram(prev => prev || null);
      setTasksError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
      setTasksLoading(false);
    }
  };

  const toggleTaskExpanded = taskId => {
    setExpandedTaskId(prev => (prev === taskId ? null : taskId));
  };

  const completeTask = async (
    taskId,
    textResponse = null,
    imageFile = null
  ) => {
    try {
      setSubmittingTasks(prev => new Set([...prev, taskId]));

      const jwt = await getVerifiedJWT();

      let response;
      if (imageFile) {
        const formData = new FormData();
        formData.append("task_id", taskId);
        if (textResponse) formData.append("text_response", textResponse);
        formData.append("image", imageFile);
        response = await fetch(getApiEndpoint("/v1/programs/task/complete"), {
          method: "POST",
          headers: { Authorization: `Bearer ${jwt}` },
          body: formData,
        });
      } else {
        const body = { task_id: taskId };
        if (textResponse) body.text_response = textResponse;
        response = await fetch(getApiEndpoint("/v1/programs/task/complete"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        });
      }

      if (!response.ok) {
        let errMsg = "Failed to complete task";
        let errData = null;
        try {
          errData = await response.json();
          errMsg = errData?.message || errMsg;
        } catch {}
        const error = new Error(errMsg);
        error.response = { data: errData || { message: errMsg } };
        throw error;
      }

      const result = await response.json();
      const completionData = result.data;

      setTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === taskId
            ? {
                ...t,
                program_user_completed_tasks: [
                  {
                    completed_at:
                      completionData.completed_at || new Date().toISOString(),
                    text_response: textResponse,
                    completed_by: t.requires_admin_approval
                      ? null
                      : completionData.completed_by,
                  },
                ],
              }
            : t
        )
      );
    } catch (err) {
      throw err;
    } finally {
      setSubmittingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const getCompletedTaskDetails = task => {
    if (
      task?.program_user_completed_tasks &&
      task.program_user_completed_tasks.length > 0
    ) {
      return task.program_user_completed_tasks[0];
    }
    return null;
  };

  const isTaskWaitingApproval = task => {
    const completedRecord = getCompletedTaskDetails(task);
    if (task.task_type === "ADMIN_APPROVE" && !completedRecord) return true;
    if (
      completedRecord &&
      !completedRecord.is_rejected &&
      task.requires_admin_approval &&
      !completedRecord.completed_by
    )
      return true;
    return false;
  };

  const isTaskRejected = task => {
    const completedRecord = getCompletedTaskDetails(task);
    return !!(completedRecord && completedRecord.is_rejected);
  };

  const isProgramTaskCompleted = task => {
    const completedRecord = getCompletedTaskDetails(task);
    if (!completedRecord) return false;
    if (task.requires_admin_approval && !completedRecord.completed_by)
      return false;
    return true;
  };

  const completedTasksCount = tasks.filter(isProgramTaskCompleted).length;
  const totalTasksCount = tasks.length;
  const todoTasksCount = tasks.filter(
    task => !isProgramTaskCompleted(task) && !isTaskWaitingApproval(task)
  ).length;
  const waitingApprovalTasksCount = tasks.filter(isTaskWaitingApproval).length;
  const completionPercentage =
    totalTasksCount > 0
      ? Math.round((completedTasksCount / totalTasksCount) * 100)
      : 0;

  const formatDate = dateString => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch {
      return "";
    }
  };

  const formatDuration = minutes => {
    if (!minutes) return "";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTaskTypeLabel = taskType => {
    switch (taskType) {
      case "VIDEO":
        return "Watch Video";
      case "UPLOAD":
        return "Upload File";
      case "USER_APPROVE":
        return "Self-Complete";
      case "ADMIN_APPROVE":
        return "Admin Approval";
      case "TEXT_RESPONSE":
        return "Text Response";
      case "PITCH_REVIEW":
        return "Pitch Review";
      default:
        return taskType;
    }
  };

  const taskTypeConfig = [
    {
      type: "VIDEO",
      label: "Watch Video",
      icon: <IconPlayerPlay size={16} />,
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      type: "USER_APPROVE",
      label: "Self-Complete",
      icon: <IconUserCheck size={16} />,
      color: "#22c55e",
      bg: "#f0fdf4",
    },
    {
      type: "TEXT_RESPONSE",
      label: "Text Response",
      icon: <IconFileText size={16} />,
      color: "#8b5cf6",
      bg: "#f5f3ff",
    },
    {
      type: "PITCH_REVIEW",
      label: "Pitch Review",
      icon: <IconStar size={16} />,
      color: "#d946ef",
      bg: "#fdf4ff",
    },
    {
      type: "UPLOAD",
      label: "Upload File",
      icon: <IconUpload size={16} />,
      color: "#f59e0b",
      bg: "#fef3c7",
    },
    {
      type: "QUIZ",
      label: "Quiz",
      icon: <IconQuestionMark size={16} />,
      color: "#06b6d4",
      bg: "#ecfeff",
    },
    {
      type: "ADMIN_APPROVE",
      label: "Admin Approval",
      icon: <IconListCheck size={16} />,
      color: "#ef4444",
      bg: "#fef2f2",
    },
  ];

  const taskTypeCounts = tasks.reduce((acc, t) => {
    acc[t.task_type] = (acc[t.task_type] || 0) + 1;
    return acc;
  }, {});

  const getFilteredTasks = () => {
    switch (activeTab) {
      case "todo":
        return tasks.filter(
          task => !isProgramTaskCompleted(task) && !isTaskWaitingApproval(task)
        );
      case "waiting":
        return tasks.filter(task => isTaskWaitingApproval(task));
      case "done":
        return tasks.filter(task => isProgramTaskCompleted(task));
      default:
        return tasks;
    }
  };

  const filteredTasks = getFilteredTasks();

  /* Measure the sidebar so the modal starts right after it.
     Uses ResizeObserver so it also responds to sidebar expand/collapse. */
  useEffect(() => {
    const nav = document.querySelector(".sidebar-navigation");
    if (!nav) return;

    const measure = () => setSidebarWidth(nav.getBoundingClientRect().width);
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  /* ── File preview / completion modal renderer ── */
  const renderFilePreviewModal = () => {
    if (!filePreviewTask) return null;

    const previewTask = filePreviewTask.task || filePreviewTask;
    const previewFileUrl = filePreviewTask.file_url || previewTask.file_url;
    const previewFileName = filePreviewTask.file_name || previewTask.file_name;

    const isUserApprove =
      previewTask.task_type === "USER_APPROVE" &&
      !previewTask.requires_image &&
      !isProgramTaskCompleted(previewTask) &&
      !isTaskWaitingApproval(previewTask) &&
      !isTaskRejected(previewTask);

    const hasFile = Boolean(previewFileUrl);

    const handleMarkComplete = async () => {
      try {
        await completeTask(previewTask.id, null, null);
        setFilePreviewTask(null);
      } catch {
        /* errors already thrown up the chain */
      }
    };

    const close = () => setFilePreviewTask(null);

    /* ── Case 1: USER_APPROVE with NO file → simple confirmation ── */
    if (isUserApprove && !hasFile) {
      return (
        <div
          className="file-preview-modal-overlay"
          style={{ left: sidebarWidth }}
          onClick={close}
        >
          <div
            className="file-preview-modal file-preview-modal--confirm"
            onClick={e => e.stopPropagation()}
            style={{
              width: 440,
              height: "auto",
              borderRadius: 14,
              boxShadow: "0 24px 64px rgba(0,0,0,.35)",
              alignSelf: "center",
              margin: "auto",
            }}
          >
            <div className="file-preview-modal-header">
              <span className="file-preview-modal-title">
                ✅{" "}
                {previewTask.title || previewTask.task_name || "Complete Task"}
              </span>
              <button className="file-preview-modal-close" onClick={close}>
                ✕
              </button>
            </div>
            <div className="file-preview-modal-confirm-body">
              <p className="file-preview-modal-confirm-text">
                Mark this task as completed?
              </p>
              <div className="file-preview-modal-confirm-actions">
                <button
                  className="file-preview-modal-cancel-btn"
                  onClick={close}
                >
                  Cancel
                </button>
                <button
                  className="file-preview-modal-complete-btn"
                  onClick={handleMarkComplete}
                  disabled={submittingTasks.has(previewTask.id)}
                >
                  {submittingTasks.has(previewTask.id)
                    ? "Completing…"
                    : "Mark as Completed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    /* ── Case 2: Has a file (USER_APPROVE or plain attachment) ── */
    if (!hasFile) return null; // nothing to show if no file and not user_approve

    const fileExt = (previewFileName || previewFileUrl || "")
      .split(".")
      .pop()
      .toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(
      fileExt
    );
    const isVideo = ["mp4", "mov", "webm", "avi"].includes(fileExt);
    const isPdf = fileExt === "pdf";
    const isSpreadsheet = ["xlsx", "xls", "csv", "ods", "numbers"].includes(
      fileExt
    );
    const isOfficeDoc = ["doc", "docx", "ppt", "pptx"].includes(fileExt);

    return (
      <div
        className="file-preview-modal-overlay"
        style={{ left: sidebarWidth }}
        onClick={close}
      >
        <div className="file-preview-modal" onClick={e => e.stopPropagation()}>
          <div className="file-preview-modal-header">
            {/* Left: open-in-new-tab replaces the title */}
            <a
              href={previewFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="file-preview-open-link"
            >
              ↗ Open in new tab
            </a>

            {/* Center: Mark as Read (USER_APPROVE only) */}
            <div
              style={{
                position: "absolute",
                left: "calc(50% + 130px)",
                transform: "translateX(-50%)",
              }}
            >
              {isUserApprove && (
                <button
                  className="file-preview-modal-complete-btn"
                  style={{
                    flex: "none",
                    padding: "6px 20px",
                    fontSize: "0.875rem",
                  }}
                  onClick={handleMarkComplete}
                  disabled={submittingTasks.has(previewTask.id)}
                >
                  {submittingTasks.has(previewTask.id)
                    ? "Completing…"
                    : "✓ Mark as Read"}
                </button>
              )}
            </div>

            {/* Right: close */}
            <div className="file-preview-modal-actions">
              <button className="file-preview-modal-close" onClick={close}>
                ✕
              </button>
            </div>
          </div>

          <div className="file-preview-modal-body">
            {isPdf && (
              <iframe
                src={previewFileUrl}
                title={previewFileName || "Document preview"}
                className="file-preview-modal-iframe"
              />
            )}
            {isImage && (
              <img
                src={previewFileUrl}
                alt={previewFileName || "Attached file"}
                className="file-preview-modal-image"
              />
            )}
            {isVideo && (
              <video
                src={previewFileUrl}
                controls
                className="file-preview-modal-video"
              />
            )}
            {(isSpreadsheet || isOfficeDoc) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 20,
                  color: "#9ca3af",
                  background: "#292929",
                }}
              >
                <div style={{ fontSize: 56 }}>
                  {isSpreadsheet ? "📊" : "📄"}
                </div>
                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color: "#e5e7eb",
                      margin: "0 0 6px",
                    }}
                  >
                    {previewFileName || "Attached File"}
                  </p>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      margin: "0 0 20px",
                      color: "#9ca3af",
                    }}
                  >
                    This file type can't be previewed in the browser.
                  </p>
                  <a
                    href={previewFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "10px 28px",
                      background: "#2563eb",
                      color: "white",
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      textDecoration: "none",
                    }}
                  >
                    ↗ Open File
                  </a>
                </div>
              </div>
            )}
            {!isPdf &&
              !isImage &&
              !isVideo &&
              !isSpreadsheet &&
              !isOfficeDoc && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 20,
                    color: "#9ca3af",
                    background: "#292929",
                  }}
                >
                  <div style={{ fontSize: 56 }}>📎</div>
                  <div style={{ textAlign: "center" }}>
                    <p
                      style={{
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        color: "#e5e7eb",
                        margin: "0 0 6px",
                      }}
                    >
                      {previewFileName || "Attached File"}
                    </p>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        margin: "0 0 20px",
                        color: "#9ca3af",
                      }}
                    >
                      This file type can't be previewed in the browser.
                    </p>
                    <a
                      href={previewFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        padding: "10px 28px",
                        background: "#2563eb",
                        color: "white",
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        textDecoration: "none",
                      }}
                    >
                      ↗ Open File
                    </a>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageContent
      customHeader={
        fromTraining ? (
          <div className="program-details-training-header">
            <div className="program-details-training-header-left">
              <h1 className="program-details-training-title">Training</h1>
              <div className="program-details-training-tabs">
                {["Home", "All Courses", "All Programs"].map(tab => (
                  <button
                    key={tab}
                    className={`program-details-training-tab ${
                      !program && trainingTab === tab ? "active" : ""
                    }`}
                    onClick={() =>
                      navigate("/training", {
                        state: { activeTab: tab },
                      })
                    }
                  >
                    {tab}
                  </button>
                ))}
                <button
                  className="program-details-training-tab active"
                  onClick={() => {}}
                >
                  {program?.program_name || "Program"}
                </button>
                <button
                  className={`program-details-training-tab ${
                    !program && trainingTab === "All Videos" ? "active" : ""
                  }`}
                  onClick={() =>
                    navigate("/training", {
                      state: { activeTab: "All Videos" },
                    })
                  }
                >
                  All Videos
                </button>
              </div>
            </div>
          </div>
        ) : undefined
      }
    >
      <div className="program-details-container">
        {loading && (
          <Box p="md" ta="center">
            <Text c="dimmed">Loading program...</Text>
          </Box>
        )}

        {error && !loading && (
          <DisplayMessage
            type="error"
            message={"Error loading program: " + error}
          />
        )}

        {!loading && program && (
          <div className="program-details-layout">
            {/* Left Sidebar */}
            <div className="program-sidebar">
              <Text
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: 8,
                  marginBottom: 4,
                }}
              >
                <LinkifiedText text={program.program_name} />
              </Text>

              {program.cover_image && (
                <div
                  style={{
                    width: "100%",
                    paddingTop: "56.25%",
                    position: "relative",
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 4,
                  }}
                >
                  <img
                    src={program.cover_image}
                    alt="Program cover"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}

              <div
                className="progress-card"
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <Group mb="md">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: "#fff7ed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconTrendingUp size={22} color="#ff6b35" />
                  </div>
                  <div>
                    <Text
                      style={{
                        fontSize: "2rem",
                        fontWeight: 700,
                        color: "#1a1a1a",
                        lineHeight: 1,
                      }}
                    >
                      {completionPercentage}%
                    </Text>
                    <Text
                      style={{
                        fontSize: "0.9375rem",
                        color: "#666",
                        fontWeight: 500,
                      }}
                    >
                      Complete
                    </Text>
                  </div>
                </Group>

                <Progress
                  value={completionPercentage}
                  size="sm"
                  radius="xl"
                  mb="md"
                  color="#ff6b35"
                />

                <Group justify="space-between" mb="lg">
                  <Text style={{ fontSize: "0.875rem", color: "#666" }}>
                    {completedTasksCount} of {totalTasksCount} tasks done
                  </Text>
                  <Text style={{ fontSize: "0.875rem", color: "#666" }}>
                    {todoTasksCount + waitingApprovalTasksCount} remaining
                  </Text>
                </Group>

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {taskTypeConfig.map(cfg => {
                    const count = taskTypeCounts[cfg.type] || 0;
                    return (
                      <div
                        key={cfg.type}
                        className="insight-row"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flex: 1,
                        }}
                      >
                        <Group gap="xs">
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              background: cfg.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: cfg.color,
                            }}
                          >
                            {cfg.icon}
                          </div>
                          <Text className="insight-label">{cfg.label}</Text>
                        </Group>
                        <span className="insight-badge">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Content - Tasks */}
            <div className="program-content">
              {/* Tabs */}
              <div className="task-tabs">
                <button
                  className={`task-tab ${activeTab === "todo" ? "active" : ""}`}
                  onClick={() => setActiveTab("todo")}
                >
                  To Do ({todoTasksCount})
                </button>
                <button
                  className={`task-tab ${activeTab === "waiting" ? "active" : ""}`}
                  onClick={() => setActiveTab("waiting")}
                >
                  Waiting Approval ({waitingApprovalTasksCount})
                </button>
                <button
                  className={`task-tab ${activeTab === "done" ? "active" : ""}`}
                  onClick={() => setActiveTab("done")}
                >
                  Done ({completedTasksCount})
                </button>
              </div>

              {/* Tasks List */}
              <div className="tasks-list">
                {tasksLoading && (
                  <Box p="xl" ta="center">
                    <Text c="dimmed">Loading tasks...</Text>
                  </Box>
                )}

                {tasksError && !tasksLoading && (
                  <Box p="xl" ta="center">
                    <Text c="red">Error loading tasks: {tasksError}</Text>
                  </Box>
                )}

                {!tasksLoading && filteredTasks.length === 0 && !tasksError && (
                  <Paper p="xl" ta="center" withBorder className="empty-state">
                    <Text size="lg" fw={600} mb="xs">
                      {activeTab === "done"
                        ? "No completed tasks yet"
                        : activeTab === "waiting"
                          ? "No tasks waiting for approval"
                          : "No tasks to do"}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {activeTab === "done"
                        ? "Complete tasks to see them here"
                        : activeTab === "waiting"
                          ? "Tasks requiring admin approval will appear here"
                          : "Keep working through your tasks!"}
                    </Text>
                  </Paper>
                )}

                {!tasksLoading &&
                  filteredTasks.map(task => {
                    const isCompleted = isProgramTaskCompleted(task);
                    const actualIndex = tasks.findIndex(t => t.id === task.id);
                    const isExpanded = expandedTaskId === task.id;

                    return (
                      <div
                        key={task.id}
                        className={`task-item ${isCompleted ? "completed" : ""}`}
                        style={{ overflow: "hidden" }}
                      >
                        <div
                          className="task-main"
                          style={{ paddingBottom: 14 }}
                          onClick={() => {
                            if (
                              task.task_type === "USER_APPROVE" &&
                              !task.requires_image &&
                              !isTaskRejected(task) &&
                              !isCompleted &&
                              !isTaskWaitingApproval(task)
                            ) {
                              // Open modal: shows doc if attached, or confirmation if not
                              setFilePreviewTask(task);
                            } else {
                              toggleTaskExpanded(task.id);
                            }
                          }}
                        >
                          {/* Checkbox */}
                          <div
                            className={`task-checkbox ${isCompleted ? "checked" : ""}`}
                          >
                            {isCompleted && (
                              <span className="checkmark">✓</span>
                            )}
                          </div>

                          {/* Task Content */}
                          <div className="task-content">
                            <Text className="task-title">
                              <LinkifiedText
                                text={
                                  task.title ||
                                  task.task_name ||
                                  `Task ${task.id}`
                                }
                              />
                            </Text>

                            {(task.description || task.task_desc) && (
                              <Text className="task-description">
                                <LinkifiedText
                                  text={task.description || task.task_desc}
                                />
                              </Text>
                            )}

                            {(task.files?.length
                              ? task.files
                              : task.file_url
                                ? [
                                    {
                                      file_url: task.file_url,
                                      file_name: task.file_name,
                                    },
                                  ]
                                : []
                            ).map((file, idx) => (
                              <button
                                key={
                                  file.id ??
                                  `${file.file_name || "file"}-${idx}`
                                }
                                className="task-file-attachment"
                                onClick={e => {
                                  e.stopPropagation();
                                  setFilePreviewTask({ ...file, task });
                                }}
                              >
                                📎{" "}
                                {file.file_name ||
                                  `View Attached File ${idx + 1}`}
                              </button>
                            ))}

                            <Group gap="xs" mt="xs" className="task-meta">
                              {task.estimated_duration && (
                                <Group gap="xs" className="meta-item">
                                  <IconClock size={14} />
                                  <Text size="xs">
                                    {formatDuration(task.estimated_duration)}
                                  </Text>
                                </Group>
                              )}

                              {task.due_date && (
                                <Group gap="xs" className="meta-item">
                                  <IconCalendar size={14} />
                                  <Text size="xs">
                                    Due {formatDate(task.due_date)}
                                  </Text>
                                </Group>
                              )}

                              {(() => {
                                const cfg = taskTypeConfig.find(
                                  c => c.type === task.task_type
                                );
                                if (!cfg)
                                  return (
                                    <span
                                      className={`task-type-badge ${task.task_type.toLowerCase()}`}
                                    >
                                      {getTaskTypeLabel(task.task_type)}
                                    </span>
                                  );
                                return (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      padding: "2px 9px",
                                      borderRadius: 20,
                                      background: cfg.bg,
                                      border: `1px solid ${cfg.color}33`,
                                      color: cfg.color,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {cfg.icon}
                                    {cfg.label}
                                  </span>
                                );
                              })()}

                              {task.requires_admin_approval && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "2px 9px",
                                    borderRadius: 20,
                                    background: "#fef2f2",
                                    border: "1px solid #fca5a5",
                                    color: "#dc2626",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  <IconShieldCheck size={11} />
                                  Admin Approval
                                </span>
                              )}

                              {task.requires_image && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "2px 9px",
                                    borderRadius: 20,
                                    background: "#f9fafb",
                                    border: "1px solid #d1d5db",
                                    color: "#374151",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  <IconUpload size={11} />
                                  Image Required
                                </span>
                              )}
                            </Group>
                          </div>

                          {/* Status badges */}
                          {submittingTasks.has(task.id) && (
                            <Box style={{ flexShrink: 0, marginTop: 2 }}>
                              <Text size="xs" c="dimmed" fw={500}>
                                Submitting...
                              </Text>
                            </Box>
                          )}
                          {isTaskWaitingApproval(task) &&
                            getCompletedTaskDetails(task) && (
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#f59e0b",
                                  background: "#fef3c7",
                                  border: "1px solid #f59e0b33",
                                  borderRadius: 20,
                                  padding: "3px 10px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Pending Review
                              </span>
                            )}
                          {isTaskRejected(task) && (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#dc2626",
                                background: "#fef2f2",
                                border: "1px solid #fca5a5",
                                borderRadius: 20,
                                padding: "3px 10px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ✕ Rejected
                            </span>
                          )}
                        </div>

                        {/* Inline task expansion */}
                        {isExpanded && (
                          <ProgramTaskModal
                            inline={true}
                            isOpen={true}
                            onClose={() => setExpandedTaskId(null)}
                            task={task}
                            taskNumber={actualIndex + 1}
                            onComplete={imageFile =>
                              completeTask(task.id, null, imageFile)
                            }
                            onTextSubmit={(textResponse, imageFile) =>
                              completeTask(task.id, textResponse, imageFile)
                            }
                            onQuizSubmit={async (
                              selectedAnswerOrArray,
                              setQuizError
                            ) => {
                              try {
                                const textResponse = Array.isArray(
                                  selectedAnswerOrArray
                                )
                                  ? JSON.stringify(selectedAnswerOrArray)
                                  : String(selectedAnswerOrArray);
                                await completeTask(task.id, textResponse, null);
                              } catch (err) {
                                let msg = "Wrong answer, try again!";
                                try {
                                  const raw =
                                    err?.message ||
                                    err?.response?.data?.message ||
                                    "";
                                  const parsed = JSON.parse(raw);
                                  if (parsed.message) msg = parsed.message;
                                } catch {
                                  if (err?.message) msg = err.message;
                                }
                                setQuizError(msg);
                                throw err;
                              }
                            }}
                            onNavigateToVideo={() =>
                              navigate("/training", {
                                state: {
                                  courseId: task?.training_video?.course_id,
                                },
                              })
                            }
                            isSubmitting={submittingTasks.has(task.id)}
                            completedDetails={getCompletedTaskDetails(task)}
                            formatDate={formatDate}
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* File Preview Modal – rendered via portal so it escapes parent stacking contexts */}
      {filePreviewTask &&
        ReactDOM.createPortal(renderFilePreviewModal(), document.body)}
    </PageContent>
  );
}

export default ProgramDetailsPage;
